import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { isReadingPaneStackedView } from 'owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils';
import isMessageListBranchExpansionType from 'owa-mail-organization-settings/lib/utils/isMessageListBranchExpansionType';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import type TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import isValidConversationCache from 'owa-mail-store/lib/selectors/isValidConversationCache';
import type { InstrumentationContext } from 'owa-search-types/lib/types/InstrumentationContext';
import { isDumpsterSearchTable } from 'owa-mail-list-search';
import type { SearchTableQuery } from 'owa-mail-list-search';
import {
    isDumpsterTable,
    MailRowDataPropertyGetter,
    updateShouldShowSelectedRowInRp,
    type TableQueryType,
    getRowKeyMailboxInfoIfUnifiedInbox,
} from 'owa-mail-list-store';
import SubstrateSearchScenario from 'owa-search-service/lib/data/schema/SubstrateSearchScenario';
import { SearchProvider } from 'owa-search-service/lib/data/schema/SearchProvider';
import { lazyLogSearchEntityActions } from 'owa-search-instrumentation';
import { lazyLoadConversation } from 'owa-mail-store-actions';
import { logCoreUsage, logGreyError, logUsage } from 'owa-analytics';
import type { PerformanceDatapoint } from 'owa-analytics';
import { lazyLoadItemReadingPaneForSingleMailItemSelected } from 'owa-mail-reading-pane-store';
import { loadConversationReadingPaneForSingleMailItemSelected } from 'owa-mail-reading-pane-store-conversation';
import { SearchScenarioId } from 'owa-search-store/lib/store/schema/SearchScenarioId';
import { getAggregationBucket } from 'owa-analytics-actions';
import { setShowListPane } from 'owa-mail-layout/lib/actions/setShowListPane';
import { setShowReadingPane } from 'owa-mail-layout/lib/actions/setShowReadingPane';
import {
    isReadingPanePositionBottom,
    isReadingPanePositionRight,
    isReadingPanePositionOff,
    isReadingPanePopoutOnly,
} from 'owa-mail-layout/lib/selectors/readingPanePosition';
import { getActiveContentTab, lazyActivateTab, primaryTab } from 'owa-tab-store';
import type { LoadConversationItemActionSource } from 'owa-mail-store';
import { getComposeViewState } from 'owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState';
import { getFolderIdForSelectedNode } from 'owa-mail-folder-forest-store';
import { getSelectMailItemDatapoint } from 'owa-mail-logging/lib/utils/selectMailItemDatapointGetterAndSetter';
import doesFolderSupportConversationGrouping from '../utils/doesFolderSupportConversationGrouping';
import endSelectMailItemDatapoint from 'owa-mail-logging/lib/utils/endSelectMailItemDatapoint';
import { lazyProcessItemForDecryption } from 'owa-addins-decryption';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import type ItemPartViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemPartViewState';
import { isAccountFeatureEnabled } from 'owa-feature-flags';
import mailStore from 'owa-mail-store/lib/store/Store';
import { lazyCreateOpenEventUnifiedEventFromMeetingRequest } from 'owa-calendar-unified-telemetry-utils';
import { flattenTelemetryData } from 'owa-calendar-unified-telemetry-utils/lib/utils/flattenTelemetryData';
import { type UTCalendarOrigin, type UTCalendarUIContext } from 'owa-unified-telemetry';
import getMeetingItem from 'owa-meeting-message/lib/utils/getMeetingItem';
import { isMeetingRequest } from 'owa-meeting-message-utils';
import type MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import { closeBizChatInReadingPane } from 'owa-copilot-chat-drawer-store';
import singleSelectRowInternal from './singleSelectRowInternal';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { trace } from 'owa-trace';
import { isProactiveDraft } from 'owa-proactive-drafts-shared';

/**
 * Handles no item selection in list view
 */
function handleListViewNoItemSelectedChange(isSingleLineView: boolean) {
    setShowListPane(true /* showListPane */);

    if (isSingleLineView) {
        setShowReadingPane(false /* showReadingPane */);
    } else {
        setShowReadingPane(true /* showReadingPane */);
    }
}

/**
 * Handles single item selection in list view
 */
function handleListViewSingleItemSelectedChangedWhenReadingPaneOff(
    rowKey: string,
    isUserNavigation: boolean,
    mailListItemSelectionSource: MailListItemSelectionSource,
    instrumentationContext: InstrumentationContext,
    tableView: TableView
): Promise<void> {
    let promiseToReturn = Promise.resolve();
    let showReadingPane = false;
    let showListPane = true;

    switch (mailListItemSelectionSource) {
        case MailListItemSelectionSource.MailListItemBody:
        case MailListItemSelectionSource.CommandBarArrows: // Only shown in immersive reading pane
        case MailListItemSelectionSource.KeyboardEnter:
        case MailListItemSelectionSource.MailListItemExpansion:
        case MailListItemSelectionSource.MailListItemRichPreview:
        case MailListItemSelectionSource.SearchSuggestionClick:
        case MailListItemSelectionSource.RouteHandler:
            showReadingPane = true;
            showListPane = false;
            break;
        case MailListItemSelectionSource.MailListItemTwisty:
            promiseToReturn = loadConversationItems(
                rowKey,
                tableView,
                'LoadConvReadingPaneLVTwisty'
            );
            showReadingPane = false;
            showListPane = true;
            break;
        case MailListItemSelectionSource.ImmersiveReadingPaneDelete:
            if (getUserConfiguration().UserOptions?.NextSelection === 'ReturnToView') {
                showReadingPane = false;
                showListPane = true;
            } else {
                showReadingPane = true;
                showListPane = false;
            }
            break;
        case MailListItemSelectionSource.KeyboardUpDown:
        case MailListItemSelectionSource.MailListItemCheckbox:
        case MailListItemSelectionSource.KeyboardCtrlSpace:
        case MailListItemSelectionSource.MailListItemContextMenu:
        case MailListItemSelectionSource.NewConversation:
        case MailListItemSelectionSource.Reset:
        case MailListItemSelectionSource.RowRemoval:
        default:
            showReadingPane = false;
            showListPane = true;
            break;
    }

    if (showReadingPane) {
        instrumentationContext?.dp?.addCheckpoint?.('OFF_LRP');

        if (isReadingPanePopoutOnly()) {
            // If enter key was pressed - treat it as a simulated double click
            if (mailListItemSelectionSource === MailListItemSelectionSource.KeyboardEnter) {
                singleSelectRowInternal(
                    tableView,
                    rowKey,
                    MailListItemSelectionSource.MailListItemSimulatedDoubleClick
                );
                return Promise.resolve();
            }
            // If an item or item part was single clicked - don't open RP
            else if (
                mailListItemSelectionSource === MailListItemSelectionSource.MailListItemBody ||
                mailListItemSelectionSource === MailListItemSelectionSource.MailListItemExpansion ||
                mailListItemSelectionSource === MailListItemSelectionSource.MailListItemRichPreview
            ) {
                return promiseToReturn;
            }
        }

        promiseToReturn = loadReadingPane(
            rowKey,
            tableView,
            isUserNavigation,
            instrumentationContext,
            true /*isSingleLineView */,
            mailListItemSelectionSource
        );
    }

    setShowReadingPane(showReadingPane);
    setShowListPane(showListPane);

    return promiseToReturn;
}

// Per-tableView state for keyboard navigation debouncing to avoid race conditions
// between multiple tableViews/accounts/tabs
interface TableViewNavigationState {
    timeoutId: ReturnType<typeof setTimeout> | null;
    pendingResolve: (() => void) | null;
    lastKeyboardSelectionTime: number;
}

const tableViewNavigationStates = new Map<string, TableViewNavigationState>();

function getOrCreateNavigationState(tableViewId: string): TableViewNavigationState {
    let state = tableViewNavigationStates.get(tableViewId);
    if (!state) {
        state = {
            timeoutId: null,
            pendingResolve: null,
            lastKeyboardSelectionTime: 0,
        };
        tableViewNavigationStates.set(tableViewId, state);
    }
    return state;
}

/**
 * Handles single item selection in list view
 */
function handleListViewSingleItemSelectedChangedWhenReadingPaneOn(
    rowKey: string,
    isUserNavigation: boolean,
    mailListItemSelectionSource: MailListItemSelectionSource,
    instrumentationContext: InstrumentationContext,
    tableView: TableView,
    eventTimestamp?: number
): Promise<void> {
    const navState = getOrCreateNavigationState(tableView.id);

    if (navState.timeoutId) {
        clearTimeout(navState.timeoutId);
        navState.timeoutId = null;

        // Resolve the previous promise since it was superseded by a new navigation
        // This is not an error condition - it's expected behavior during rapid keyboard navigation
        if (navState.pendingResolve) {
            navState.pendingResolve();
            navState.pendingResolve = null;
        }
    }

    // If the message list is branch expansion type we need to call GCI to populate itemparts if
    // Conversation is not in mailstore yet AND
    // Folder supports conversations. Eg. junk / drafts folders do not support conversations and always contain individual items.
    if (isMessageListBranchExpansionType() && doesFolderSupportConversationGrouping()) {
        loadConversationItems(rowKey, tableView, 'LoadConvReadingPaneUnstacked');
    }

    if (mailListItemSelectionSource !== MailListItemSelectionSource.MailListItemContextMenu) {
        updateShouldShowSelectedRowInRp(true);
        setShowReadingPane(true /* showReadingPane */);
    } else {
        updateShouldShowSelectedRowInRp(false);
    }

    setShowListPane(true /* showListPane */);

    // [Perf optimization] If user is moving through the listview via keyboard up/down arrows, add a slight delay
    // to avoid us having to load every conversation/item if user is just trying to scroll (each stroke is within a small window).
    if (mailListItemSelectionSource === MailListItemSelectionSource.KeyboardUpDown) {
        const currentKeyboardSelectionTime = Date.now();
        const selectionTimeDiff = currentKeyboardSelectionTime - navState.lastKeyboardSelectionTime;
        navState.lastKeyboardSelectionTime = currentKeyboardSelectionTime;

        if (
            selectionTimeDiff <= 500 // milliseconds
        ) {
            return new Promise<void>((resolve, reject) => {
                // Store the resolve function so we can resolve this promise if it gets cancelled
                // by subsequent navigation (which is not an error - it's expected behavior)
                navState.pendingResolve = resolve;

                instrumentationContext?.dp?.addCheckpoint?.('ON_LRP_TO');
                navState.timeoutId = setTimeout(() => {
                    navState.timeoutId = null;
                    navState.pendingResolve = null; // Clear the resolve function

                    // Verify the rowKey is still selected before loading
                    // This prevents loading stale data if selection changed during the timeout
                    if (!tableView.selectedRowKeys.has(rowKey)) {
                        resolve();
                        return;
                    }

                    loadReadingPane(
                        rowKey,
                        tableView,
                        isUserNavigation,
                        instrumentationContext,
                        false /*isSingleLineView */,
                        mailListItemSelectionSource,
                        eventTimestamp
                    )
                        .then(resolve)
                        .catch(reject);
                }, 250);
            });
        }
    }

    instrumentationContext?.dp?.addCheckpoint?.('ON_LRP');
    return loadReadingPane(
        rowKey,
        tableView,
        isUserNavigation,
        instrumentationContext,
        false /*isSingleLineView */,
        mailListItemSelectionSource,
        eventTimestamp
    );
}

function loadReadingPane(
    rowKey: string,
    tableView: TableView,
    isUserNavigation: boolean,
    instrumentationContext: InstrumentationContext,
    isSingleLineView: boolean,
    mailListItemSelectionSource: MailListItemSelectionSource,
    eventTimestamp?: number
) {
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);

    // Guard against invalid rowId from stale rowKey
    if (!rowId || !rowId.Id) {
        return Promise.resolve();
    }

    const isDraft = MailRowDataPropertyGetter.getIsDraft(rowKey, tableView);
    const isSubmitted = MailRowDataPropertyGetter.getIsSubmitted(rowKey, tableView);

    // load RP for outbox item since it should be read-only by default unless user clicks pencil button to edit
    let isFromOutbox: boolean = false;
    const folderId = getFolderIdForSelectedNode();
    if (folderId && folderIdToName(folderId) === 'outbox') {
        isFromOutbox = true;
    }

    // Check if this is a scheduled send draft or copilot-generated draft
    // These should not auto-open in compose mode
    let isScheduledSend = false;
    let isProactiveDraftItem = false;
    let shouldLoadCompose = false;
    if (isDraft && !isSubmitted && !isFromOutbox) {
        const item = MailRowDataPropertyGetter.getItem(rowKey, tableView);
        if (item) {
            isScheduledSend = !!item.DeferredSendTime;
            isProactiveDraftItem = isProactiveDraft(item);

            const isSpecialDraft = isScheduledSend || isProactiveDraftItem;
            shouldLoadCompose = !isSpecialDraft;
        }
    }
    const shouldNotFocusInCompose =
        shouldLoadCompose &&
        mailListItemSelectionSource == MailListItemSelectionSource.KeyboardUpDown;
    if (shouldLoadCompose) {
        // SMI tracks e2e from selection in ML to loading RP. Invalidate the dp for compose as we are not rendering RP components
        endSelectMailItemDatapoint('' /* contentType */, true /* shouldInvalidate */);
    }
    const readingPaneActionSource = 'ListViewSelectionChange';

    switch (tableView.tableQuery.listViewType) {
        case 0:
            // Unstacked view is where reading pane show a single item instead of itemparts stacked one below the other.
            if (!isReadingPaneStackedView(tableView.tableQuery.listViewType)) {
                return lazyLoadItemReadingPaneForSingleMailItemSelected
                    .importAndExecute(
                        MailRowDataPropertyGetter.getRowIdToShowInReadingPane(rowKey, tableView),
                        readingPaneActionSource,
                        isUserNavigation,
                        instrumentationContext,
                        shouldLoadCompose,
                        shouldNotFocusInCompose,
                        'UnstackedConversationView',
                        isSingleLineView,
                        rowId?.Id // we need to pass in the conversationId to load attachments in SHV3
                    )
                    .then(() => {
                        const itemToDecrypt = MailRowDataPropertyGetter.getRowIdToShowInReadingPane(
                            rowKey,
                            tableView
                        );
                        const itemID = itemToDecrypt?.Id;

                        if (itemID) {
                            const itemPartViewState = getItemReadingPaneViewState(itemID);
                            const updatedItem = mailStore().items.get(itemID);

                            if (
                                updatedItem &&
                                isAccountFeatureEnabled('addin-decryption', updatedItem.MailboxInfo)
                            ) {
                                if (!itemPartViewState || !itemPartViewState.itemViewState) {
                                    trace.warn(
                                        `[Decryption] itemViewState is undefined for Conversation. ItemId: ${itemID}`
                                    );
                                    return Promise.resolve();
                                }
                                return lazyProcessItemForDecryption.importAndExecute(
                                    updatedItem,
                                    itemPartViewState.itemViewState as ItemPartViewState
                                );
                            }
                        }
                        return Promise.resolve();
                    });
            }

            return loadConversationReadingPaneForSingleMailItemSelected(
                rowId,
                readingPaneActionSource,
                isUserNavigation,
                instrumentationContext,
                MailRowDataPropertyGetter.getSubject(rowKey, tableView),
                MailRowDataPropertyGetter.getCategories(rowKey, tableView),
                MailRowDataPropertyGetter.getCopilotInboxScore(rowKey, tableView),
                mailListItemSelectionSource,
                isSingleLineView,
                rowKey,
                tableView
            ).then(() => {
                logMeetingRequestOpenFromSelection(
                    rowKey,
                    tableView,
                    mailListItemSelectionSource,
                    instrumentationContext?.dp
                );
            });

        case 1:
            const item = MailRowDataPropertyGetter.getItem(rowKey, tableView);
            return lazyLoadItemReadingPaneForSingleMailItemSelected
                .importAndExecute(
                    rowId,
                    readingPaneActionSource,
                    isUserNavigation,
                    instrumentationContext,
                    shouldLoadCompose,
                    shouldNotFocusInCompose,
                    'ItemView',
                    isSingleLineView,
                    undefined /*conversationId*/,
                    undefined /*state*/,
                    eventTimestamp
                )
                .then(() => {
                    if (item && isAccountFeatureEnabled('addin-decryption', item.MailboxInfo)) {
                        const itemID = item.ItemId?.Id;

                        if (itemID) {
                            const itemPartViewState = getItemReadingPaneViewState(itemID);
                            const updatedItem = mailStore().items.get(itemID);

                            if (updatedItem) {
                                if (!itemPartViewState || !itemPartViewState.itemViewState) {
                                    trace.warn(
                                        `[Decryption] itemViewState is undefined for Message. ItemId: ${itemID}`
                                    );
                                    return Promise.resolve();
                                }
                                return lazyProcessItemForDecryption.importAndExecute(
                                    updatedItem,
                                    itemPartViewState.itemViewState as ItemPartViewState
                                );
                            }
                        }
                    }
                    return Promise.resolve();
                });

        default:
            return Promise.resolve();
    }
}

/**
 * Called when selection in list view changes to orchestrate what happens next
 * @param tableView The tableview
 * @param isUserNavigation Did user initiate selection
 * @param mailListItemSelectionSource The source of selection on mail item
 */
export default async function onListViewSelectionChange(
    tableView: TableView,
    isUserNavigation: boolean,
    mailListItemSelectionSource: MailListItemSelectionSource,
    eventTimestamp?: number
): Promise<void> {
    if (
        mailListItemSelectionSource == MailListItemSelectionSource.MessageAd ||
        mailListItemSelectionSource == MailListItemSelectionSource.GroupHeaderContextMenu
    ) {
        // return when MailListItemSelectionSource is MessageAd as selectedRowsCount is 0 at this time
        // We should not enter the handleListViewNoItemSelectedChange block to change the reading pane hide/show status.
        // No need to orchestrate anything when GroupHeaderContextMenu is selected
        return;
    }
    let promiseToReturn = Promise.resolve();
    const selectedRowsCount = tableView.selectedRowKeys.size;
    const originalActiveTab = getActiveContentTab();
    const isSingleLineView = isReadingPanePositionOff();
    const isRpPositionRight = isReadingPanePositionRight();

    if (selectedRowsCount == 0) {
        handleListViewNoItemSelectedChange(isSingleLineView);
    } else if (
        // If only one row is selected and we are not in SLV checked mode, go into one selected row logic,
        // otherwise go into muliselect case.
        selectedRowsCount == 1 &&
        !(isSingleLineView && tableView.isInCheckedMode)
    ) {
        const rowKey = [...tableView.selectedRowKeys.keys()][0];
        const tableQuery = tableView.tableQuery;
        const shouldAlwaysShowMultiselectRP =
            isDumpsterTable(tableQuery) || isDumpsterSearchTable(tableQuery);
        const isSearchTable = tableQuery.type == 1;

        const instrumentationContext = getInstrumentationContextsFromTableView(
            [rowKey],
            tableView
        )[0];

        const selectMailItemDatapoint = getSelectMailItemDatapoint();
        if (!shouldAlwaysShowMultiselectRP && instrumentationContext && selectMailItemDatapoint) {
            instrumentationContext.dp = selectMailItemDatapoint;
        }

        if (
            getUserConfiguration().SessionSettings?.IsSubstrateSearchServiceAvailable &&
            instrumentationContext
        ) {
            // 3S instrumentation
            const referenceKey =
                instrumentationContext.referenceKey ||
                (instrumentationContext.index + 1).toString();
            lazyLogSearchEntityActions.importAndExecute(
                SubstrateSearchScenario.Mail,
                '',
                '',
                instrumentationContext.logicalId || '',
                undefined /* traceId */,
                referenceKey,
                'EntityClicked'
            );
        }
        if (isSearchTable) {
            // Client search instrumentation
            logSearchResultSelectedDatapoint(rowKey, tableView);
        }

        if (!shouldAlwaysShowMultiselectRP) {
            if (isRpPositionRight || isReadingPanePositionBottom()) {
                // Ensure BizChat is not shown as wide pane, since it replaces the reading pane.
                // Collapse the drawer to its minimum width so the message column has room to render.
                if (isRpPositionRight) {
                    closeBizChatInReadingPane(window, true /* shouldCollapseDrawer */);
                }
                promiseToReturn = handleListViewSingleItemSelectedChangedWhenReadingPaneOn(
                    rowKey,
                    isUserNavigation,
                    mailListItemSelectionSource,
                    instrumentationContext,
                    tableView,
                    eventTimestamp
                );
            } else if (isSingleLineView) {
                promiseToReturn = handleListViewSingleItemSelectedChangedWhenReadingPaneOff(
                    rowKey,
                    isUserNavigation,
                    mailListItemSelectionSource,
                    instrumentationContext,
                    tableView
                );
            }
        }
    } else {
        // Multi-select case
        if (isSingleLineView) {
            setShowReadingPane(false /* showReadingPane */);
            setShowListPane(true /* showListPane */);
        }
    }
    // We should activate the primary tab and switch from the current tab if
    // the active tab is not the primary
    // the original tab is still in view (active tab has not been changed during this function)
    // and one of the following is true:
    // - it is user navigation
    // - user is in SLV
    // - or the action source is not reset
    // - there is no active compose when removing a row
    const activeTab = getActiveContentTab();
    if (
        activeTab != primaryTab &&
        originalActiveTab == activeTab &&
        (isUserNavigation ||
            isSingleLineView ||
            mailListItemSelectionSource !== MailListItemSelectionSource.Reset) &&
        !(
            mailListItemSelectionSource == MailListItemSelectionSource.RowRemoval &&
            getComposeViewState()
        )
    ) {
        lazyActivateTab.importAndExecute(primaryTab);
    }
    return promiseToReturn;
}

function loadConversationItems(
    rowKey: string,
    tableView: TableView,
    actionSource: LoadConversationItemActionSource
): Promise<void> {
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
    // If has valid conversation cache, no need to load item parts
    if (isValidConversationCache(rowId.Id)) {
        return Promise.resolve();
    }
    return lazyLoadConversation.importAndExecute(rowId, actionSource);
}

const logSearchResultSelectedDatapoint = (rowKey: string, tableView: TableView) => {
    const parentFolderId = MailRowDataPropertyGetter.getParentFolderId(rowKey, tableView);

    /**
     * Check if we know the parent folder ID of the selected item,
     * because it's necessary for data logging. In the case of selecting
     * an email suggestion from search results, we DO NOT have this
     * information, so we can't log the search result selection.
     */
    if (!parentFolderId) {
        return;
    }

    /**
     * Log datapoint that tells us about the search result that
     * was clicked.
     */
    const buckets = [10, 25, 75, 150, 250];
    const searchProvider = (tableView.tableQuery as SearchTableQuery)?.searchProvider;
    const additionalData = [
        MailRowDataPropertyGetter.getExecuteSearchSortOrder(rowKey, tableView) === 'Relevance',
        folderIdToName(parentFolderId),
        getAggregationBucket({
            value: tableView.rowKeys.indexOf(rowKey),
            buckets,
        }),
        getAggregationBucket({
            value: tableView.rowKeys.length,
            buckets,
        }),
        SearchScenarioId.Mail,
    ];

    const mailboxInfo = getRowKeyMailboxInfoIfUnifiedInbox(
        getMailboxInfo(tableView),
        tableView,
        rowKey
    );

    switch (searchProvider) {
        case SearchProvider.Offline:
            logUsage('OfflineSearch_ResultSelected', mailboxInfo, additionalData);
            break;
        case SearchProvider.PST:
            logCoreUsage('PSTSearch_ResultSelected', mailboxInfo, additionalData);
            break;
        case SearchProvider.UnifiedInbox:
            logUsage('UnifiedInboxSearch_ResultSelected', mailboxInfo, additionalData);
            break;
        default:
            logUsage('SearchResultSelected', mailboxInfo, additionalData);
            break;
    }
};

function logMeetingRequestOpenFromSelection(
    rowKey: string,
    tableView: TableView,
    selectionSource: MailListItemSelectionSource,
    datapoint?: PerformanceDatapoint
): void {
    const meetingRequest = getMeetingRequestFromRow(rowKey, tableView);
    if (!meetingRequest) {
        return;
    }
    const mailboxInfo = getRowKeyMailboxInfoIfUnifiedInbox(
        getMailboxInfo(tableView),
        tableView,
        rowKey
    );
    const origin = getOriginForSelectionSource(selectionSource);
    lazyCreateOpenEventUnifiedEventFromMeetingRequest
        .import()
        .then(createOpenEventUnifiedEventFromMeetingRequest => {
            const unifiedEvent = createOpenEventUnifiedEventFromMeetingRequest(
                meetingRequest,
                'MailList',
                origin,
                'MailReadingPane'
            );
            if (datapoint) {
                datapoint.addUnifiedTelemetryData(unifiedEvent);
                return;
            }

            const flattenedUnifiedTelemetryData = flattenTelemetryData(unifiedEvent);

            logUsage(
                'MailReadingPaneOpenMeetingRequest',
                mailboxInfo,
                flattenedUnifiedTelemetryData as any,
                {
                    unifiedTelemetry: unifiedEvent,
                }
            );
        })
        .catch(error => {
            logGreyError('MailOpenEventUnifiedTelemetryFailed', mailboxInfo, error, {
                source: 'onListViewSelectionChange',
                tableType: tableView.tableQuery.type,
                listViewType: tableView.tableQuery.listViewType,
                selectionSource,
                hasDatapoint: !!datapoint,
            });
        });
}
function getOriginForSelectionSource(
    selectionSource: MailListItemSelectionSource
): UTCalendarOrigin | undefined {
    switch (selectionSource) {
        case MailListItemSelectionSource.KeyboardEnter:
            return 'KeyboardShortcut';
        case MailListItemSelectionSource.MailListItemBody:
            return 'ListItem';
        default:
            return undefined;
    }
}
function getMeetingRequestFromRow(
    rowKey: string,
    tableView: TableView
): MeetingRequestMessageType | null {
    const itemIds = MailRowDataPropertyGetter.getItemIds(rowKey, tableView);
    if (!itemIds || itemIds.length === 0) {
        return null;
    }
    for (const itemId of itemIds) {
        const { meetingItem } = getMeetingItem(itemId);
        if (meetingItem && isMeetingRequest(meetingItem.ItemClass)) {
            return meetingItem;
        }
    }
    return null;
}
